/* src/components/inbox/ThreadPane.jsx
 * Inbox conversation detail — header (stage/value/operator), thread, composer.
 * Composer: NOTE + EMAIL live; SMS chip disabled (flags.js).
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEAL_STAGES, dealStageLabel } from './chips';
import { INBOX_EMAIL_COMPOSER, INBOX_SMS_COMPOSER } from '../../utils/flags';
import { renderMessageHtml } from '../../utils/renderMessageHtml';
import { isFreeEmailProviderDomain, leadEmailDomain } from '../../utils/freeEmailProviders';
import { smsApi } from '../../utils/api';

function formatBubbleTime(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function messageChannelLabel(msg, idx, isFirstLeadMessage) {
  const isSms = msg.channel === 'sms';
  const isChat = msg.channel === 'chat';
  if (msg.channel === 'voice') return 'VOICE';
  if (msg.type === 'lead') {
    if (isFirstLeadMessage && isChat) return 'CHAT';
    if (isFirstLeadMessage) return 'FORM';
    if (isChat) return 'CHAT';
    return isSms ? 'SMS' : 'EMAIL';
  }
  if (msg.type === 'operator') return isSms ? 'OPERATOR \u00b7 SMS' : 'OPERATOR \u00b7 EMAIL';
  if (isChat) return 'CHAT';
  return isSms ? 'SMS' : 'EMAIL';
}

function VoiceBubble({ workspaceId, callSid, label, isOwner }) {
  const [src, setSrc] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  if (!isOwner) {
    return (
      <div>
        <div>{label}</div>
        <div className="mono" style={{ fontSize: '11px', color: 'var(--dim)', marginTop: '6px' }}>
          Ask the workspace owner to play this voicemail.
        </div>
      </div>
    );
  }

  async function load() {
    if (src || loading || !workspaceId || !callSid) return;
    setLoading(true);
    setError(null);
    try {
      const url = await smsApi.getVoiceRecordingBlob(workspaceId, callSid);
      setSrc(url);
    } catch (e) {
      setError(e.message || 'Could not load voicemail');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <div>{label}</div>
      {src ? (
        <audio controls src={src} />
      ) : (
        <button
          type="button"
          className="inbox-pill-btn"
          style={{ marginTop: '8px' }}
          onClick={load}
          disabled={loading || !workspaceId || !callSid}
        >
          {loading ? 'Loading…' : 'Play voicemail'}
        </button>
      )}
      {error && (
        <div className="mono" style={{ fontSize: '11px', color: 'var(--warn)', marginTop: '6px' }}>
          {error}
        </div>
      )}
    </div>
  );
}

function smsComposerDisabledReason(conversation, smsReady) {
  if (!conversation?.leadPhone) return 'No phone number on file';
  if (conversation.smsConsent !== true) return 'Lead has not consented to SMS';
  if (conversation.smsOptedOut === true) return 'Lead opted out of SMS';
  if (!smsReady) return 'SMS number not active or A2P not approved';
  return null;
}

export function getSmsTabPresentation({
  smsStatusLoading,
  smsComposerEnabled,
  workspaceSmsLive,
  conversation,
  isOwner,
}) {
  if (smsStatusLoading && INBOX_SMS_COMPOSER) {
    return { label: 'SMS', suffix: null, disabled: true, loading: true, tooltip: undefined, setupHref: null };
  }
  if (!INBOX_SMS_COMPOSER) {
    return { label: 'SMS', suffix: 'SOON', disabled: true, loading: false, tooltip: 'Coming soon', setupHref: null };
  }
  if (!smsComposerEnabled) {
    if (workspaceSmsLive) {
      return {
        label: 'SMS',
        suffix: null,
        disabled: true,
        loading: false,
        tooltip: 'SMS number not active or lead has not opted in',
        setupHref: null,
      };
    }
    return {
      label: 'SMS',
      suffix: 'SET UP',
      disabled: !isOwner,
      loading: false,
      tooltip: isOwner ? 'Set up text messaging' : 'Ask your workspace owner to set up texting',
      setupHref: isOwner ? '/text-messaging' : null,
    };
  }
  if (conversation?.smsOptedOut === true) {
    return {
      label: 'SMS',
      suffix: 'OPTED OUT',
      disabled: true,
      loading: false,
      tooltip: 'This lead texted STOP. They must text START to re-subscribe — you cannot re-enable this.',
      setupHref: null,
    };
  }
  if (!conversation?.leadPhone || conversation.smsConsent !== true) {
    return {
      label: 'SMS',
      suffix: 'NO CONSENT',
      disabled: true,
      loading: false,
      tooltip: "This lead didn't opt in to SMS on the form.",
      setupHref: null,
    };
  }
  return { label: 'SMS', suffix: null, disabled: false, loading: false, tooltip: undefined, setupHref: null };
}

export default function ThreadPane({
  conversation,
  form,
  effectiveStage,
  effectiveDealValue,
  dealValueInput,
  onDealValueInputChange,
  onDealValueCommit,
  onStageChange,
  onToggleOperator,
  actionLoading,
  onMarkContacted,
  onMarkBooked,
  teamMembers,
  onDelegate,
  emailRecipient,
  onEmailRecipientChange,
  onEmailToTeam,
  recentEmails,
  noteText,
  onNoteTextChange,
  onAddNote,
  emailText,
  onEmailTextChange,
  onSendEmail,
  emailSendError,
  onClearEmailError,
  smsText,
  onSmsTextChange,
  onSendSms,
  smsSendError,
  onClearSmsError,
  smsComposerEnabled,
  smsReady,
  smsStatusLoading,
  mobile,
  onBack,
  onMarkSpamAndBlock,
  onUnblock,
  onApproveSuspicious,
  isOwner,
  onRetryConsentLedger,
}) {
  const [stageMenuOpen, setStageMenuOpen] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [delegateOpen, setDelegateOpen] = useState(false);
  const [delegateTo, setDelegateTo] = useState('');
  const [emailPickerOpen, setEmailPickerOpen] = useState(false);
  const [composerMode, setComposerMode] = useState('note');
  const workspaceId = conversation?.workspaceId || form?.workspaceId || '';
  const navigate = useNavigate();

  function switchComposerMode(mode) {
    setComposerMode(mode);
    if (onClearEmailError) onClearEmailError();
    if (onClearSmsError) onClearSmsError();
  }

  const smsTab = getSmsTabPresentation({
    smsStatusLoading,
    smsComposerEnabled,
    workspaceSmsLive: smsReady,
    conversation,
    isOwner,
  });
  const smsDisabledReason = smsComposerEnabled
    ? smsComposerDisabledReason(conversation, smsReady)
    : (smsTab.tooltip || 'Coming soon');

  const composerValue = composerMode === 'email'
    ? (emailText ?? '')
    : composerMode === 'sms'
      ? (smsText ?? '')
      : (noteText ?? '');
  const composerPlaceholder = composerMode === 'note'
    ? 'Add a note for your team\u2026'
    : composerMode === 'email'
      ? 'Write your reply to the lead\u2026'
      : composerMode === 'sms'
        ? (smsDisabledReason || 'Write your SMS to the lead\u2026')
        : 'Coming soon';
  const canSendNote = composerMode === 'note' && noteText?.trim() && !actionLoading;
  const canSendEmail = composerMode === 'email' && emailText?.trim() && !actionLoading;
  const canSendSms = composerMode === 'sms' && smsText?.trim() && !actionLoading && !smsDisabledReason;
  if (!conversation) {
    return (
      <div
        style={{
          flex: 1,
          minWidth: mobile ? '0' : '636px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--dim)',
          fontSize: '14px',
          padding: '3rem 1rem',
        }}
      >
        Select a lead to view the conversation.
      </div>
    );
  }

  const history = conversation.conversationHistory || [];
  const firstLeadIdx = history.findIndex((m) => m.type === 'lead');
  const isPaused = conversation.aiEnabled === false;
  const isBlocked = conversation.status === 'blocked';
  const isBuyingHeld = conversation.reviewFlag === 'buying_held_suspicious' && !isBlocked;
  const isSuspicious = conversation.spamStatus === 'suspicious' && !isBlocked && !isBuyingHeld;
  const spamReasons = conversation.spamReasons || [];
  const reasonsText = spamReasons.length > 0
    ? spamReasons.join('; ')
    : 'This lead was flagged by the spam firewall';
  const leadDomain = leadEmailDomain(conversation.leadEmail);
  const domainBlockDisabled = isFreeEmailProviderDomain(leadDomain);
  const domainBlockReason = domainBlockDisabled
    ? `Can't block @${leadDomain} — it would block real leads. Block this address instead.`
    : null;

  return (
    <div
      style={{
        flex: 1,
        minWidth: mobile ? '0' : '636px',
        display: 'flex',
        flexDirection: 'column',
        height: mobile ? 'auto' : '100vh',
        position: mobile ? 'static' : 'sticky',
        top: 0,
      }}
    >
      {mobile && onBack && (
        <button
          type="button"
          onClick={onBack}
          className="mono"
          style={{
            padding: '10px 16px',
            border: 'none',
            borderBottom: '1px solid var(--hairline-2)',
            background: 'var(--surface)',
            color: 'var(--dim)',
            fontSize: '11px',
            textAlign: 'left',
            cursor: 'pointer',
          }}
        >
          {'\u2190'} Back to inbox
        </button>
      )}
      {/* Header */}
      <div
        style={{
          padding: '16px 24px',
          borderBottom: '1px solid var(--hairline-3)',
          background: 'var(--surface)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '17px', fontWeight: 800, fontStretch: '115%', color: 'var(--ink)' }}>
              {conversation.leadName || 'Unknown'}
            </span>
            {isBuyingHeld && (
              <span className="inbox-chip warn">NEEDS YOUR OK</span>
            )}
            {isBuyingHeld && (
              <span className="inbox-chip accent-ink">AI HELD · BUYER</span>
            )}
            {conversation.spamStatus === 'suspicious' && !isBuyingHeld && (
              <span className="inbox-chip warn">LIKELY SPAM{conversation.spamScore != null ? ` \u00b7 ${conversation.spamScore}` : ''}</span>
            )}
            {conversation.spamStatus === 'clean' && (
              <span className="inbox-chip accent-ink">
                CLEAN{conversation.spamScore != null ? ` \u00b7 ${conversation.spamScore}` : ''}
              </span>
            )}
            {conversation.spamStatus && conversation.spamStatus !== 'clean' && conversation.spamStatus !== 'suspicious' && (
              <span className="inbox-chip warn">
                {conversation.spamStatus.toUpperCase()}
                {conversation.spamScore != null ? ` \u00b7 SCORE ${conversation.spamScore}` : ''}
              </span>
            )}
            {conversation.reviewFlag === 'auto_paused_ai_flagged_scam' && (
              <span className="inbox-chip warn">AI FLAGGED</span>
            )}
            {conversation.reviewFlag === 'auto_paused_loop_suspected' && (
              <span className="inbox-chip warn">LOOP PAUSED</span>
            )}
            {conversation.reviewFlag === 'auto_paused_selling_to_us' && (
              <span className="inbox-chip warn">SELLING TO YOU</span>
            )}
            {conversation.reviewFlag === 'auto_paused_intent_unclear' && (
              <span className="inbox-chip warn">INTENT UNCLEAR</span>
            )}
            {conversation.reviewFlag === 'buying_auto_released' && (
              <span className="inbox-chip accent-ink">AUTO-APPROVED · BUYER</span>
            )}
            {conversation.reviewFlag === 'lead_proposed_meeting' && (
              <span className="inbox-chip warn">NEEDS YOU · THEY SENT A BOOKING LINK</span>
            )}
            {conversation.reviewFlag === 'needs_reply' && (
              <span className="inbox-chip warn">NEEDS YOU · REPLY</span>
            )}
            {isBlocked && <span className="inbox-chip danger">BLOCKED</span>}
            {conversation.isTest && <span className="inbox-chip">TEST</span>}
            {conversation.isResponseTest && (
              <span className="inbox-chip">RESPONSE TEST</span>
            )}
            {conversation.smsConsent === true && (
              <span className="inbox-chip accent-ink">SMS</span>
            )}
          </div>
          <div className="mono" style={{ fontSize: '11px', color: 'var(--dim)', marginTop: '4px' }}>
            {conversation.leadEmail || 'no email'}
            {conversation.leadPhone ? ` \u00b7 ${conversation.leadPhone}` : ''}
            {form?.name ? ` \u00b7 via ${form.name}` : ''}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', position: 'relative', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative' }}>
            <button type="button" className="inbox-pill-btn" onClick={() => setStageMenuOpen((v) => !v)}>
              Stage: {dealStageLabel(effectiveStage)}
              <span className="mono" style={{ fontSize: '9px', color: 'var(--dim)' }}>{'\u25be'}</span>
            </button>
            {stageMenuOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  right: 0,
                  background: 'var(--surface)',
                  border: '1px solid var(--hairline-3)',
                  borderRadius: '8px',
                  padding: '6px',
                  zIndex: 20,
                  minWidth: '160px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                }}
              >
                {DEAL_STAGES.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    disabled={actionLoading}
                    onClick={() => {
                      onStageChange(s.id, effectiveDealValue);
                      setStageMenuOpen(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      textAlign: 'left',
                      background: s.id === effectiveStage ? 'color-mix(in srgb, var(--accent) 28%, transparent)' : 'transparent',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontFamily: "'Archivo', sans-serif",
                      color: 'var(--ink)',
                      fontWeight: s.id === effectiveStage ? 600 : 400,
                    }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', border: '1px solid var(--hairline-5)', borderRadius: '999px', padding: '5px 6px 5px 14px', fontSize: '12px', fontWeight: 600, color: 'var(--ink)' }}>
            $
            <input
              type="number"
              min="0"
              step="any"
              placeholder="0"
              value={dealValueInput}
              onChange={(e) => onDealValueInputChange(e.target.value)}
              onBlur={onDealValueCommit}
              style={{ width: '70px', border: 'none', background: 'transparent', fontSize: '12px', fontWeight: 600, color: 'var(--ink)', fontFamily: "'Archivo', sans-serif" }}
            />
          </div>

          <button
            type="button"
            onClick={onToggleOperator}
            disabled={actionLoading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'var(--ink)',
              color: 'var(--bg)',
              border: 'none',
              borderRadius: '999px',
              padding: '7px 14px',
              fontSize: '12px',
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: "'Archivo', sans-serif",
            }}
          >
            {isPaused ? 'Operator paused' : 'Operator on'}
            <span style={{ display: 'inline-block', width: '26px', height: '15px', borderRadius: '999px', background: 'rgba(247,248,243,0.25)', position: 'relative' }}>
              <span style={{ position: 'absolute', left: isPaused ? '2px' : '13px', top: '2px', width: '11px', height: '11px', borderRadius: '50%', background: 'var(--bg)', transition: 'left 0.15s' }} />
            </span>
          </button>

          {isBlocked && onUnblock && (
            <button
              type="button"
              className="inbox-pill-btn-primary"
              disabled={actionLoading}
              onClick={() => onUnblock()}
            >
              Unblock
            </button>
          )}

          <div style={{ position: 'relative' }}>
            <button type="button" className="inbox-pill-btn" onClick={() => setOverflowOpen((v) => !v)} aria-label="More actions">
              {'\u22ef'}
            </button>
            {overflowOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  right: 0,
                  background: 'var(--surface)',
                  border: '1px solid var(--hairline-3)',
                  borderRadius: '8px',
                  padding: '6px',
                  zIndex: 20,
                  minWidth: '190px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px',
                }}
              >
                {!isBlocked && onMarkSpamAndBlock && (
                  <button
                    type="button"
                    disabled={actionLoading}
                    onClick={() => { setBlockModalOpen(true); setOverflowOpen(false); }}
                    style={{ ...overflowItemStyle, color: 'var(--danger)' }}
                  >
                    Mark as spam / block sender
                  </button>
                )}
                <button
                  type="button"
                  disabled={actionLoading || conversation.manuallyContacted}
                  onClick={() => { onMarkContacted(); setOverflowOpen(false); }}
                  style={overflowItemStyle}
                >
                  {conversation.manuallyContacted ? 'Contacted' : 'Mark contacted'}
                </button>
                <button
                  type="button"
                  disabled={actionLoading || conversation.manuallyBooked}
                  onClick={() => { onMarkBooked(); setOverflowOpen(false); }}
                  style={overflowItemStyle}
                >
                  {conversation.manuallyBooked ? 'Booked' : 'Mark booked'}
                </button>
                <button
                  type="button"
                  disabled={actionLoading}
                  onClick={() => { setEmailPickerOpen(true); setOverflowOpen(false); }}
                  style={overflowItemStyle}
                >
                  Email to team
                </button>
                <button
                  type="button"
                  disabled={actionLoading}
                  onClick={() => { setDelegateOpen(true); setOverflowOpen(false); }}
                  style={overflowItemStyle}
                >
                  Assign
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {blockModalOpen && (
        <div
          style={{
            margin: '12px 24px 0',
            padding: '16px',
            border: '1px solid var(--hairline-3)',
            borderRadius: '10px',
            background: 'var(--surface)',
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: '8px', color: 'var(--ink)' }}>
            Mark as spam / block sender
          </div>
          <p style={{ margin: '0 0 12px', fontSize: '13px', color: 'var(--muted)' }}>
            This stops AI replies and moves the thread to Spam/Blocked. You can unblock later.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <button
              type="button"
              className="inbox-pill-btn-primary"
              disabled={actionLoading}
              onClick={async () => {
                const r = await onMarkSpamAndBlock?.({ blockDomain: false });
                if (r?.success) setBlockModalOpen(false);
              }}
            >
              Block this address
            </button>
            <button
              type="button"
              className="inbox-pill-btn"
              disabled={actionLoading || domainBlockDisabled}
              title={domainBlockReason || undefined}
              onClick={async () => {
                const r = await onMarkSpamAndBlock?.({ blockDomain: true });
                if (r?.success) setBlockModalOpen(false);
              }}
            >
              Block whole domain{leadDomain ? ` (@${leadDomain})` : ''}
            </button>
            {domainBlockReason && (
              <p className="mono" style={{ margin: 0, fontSize: '11px', color: 'var(--warn)' }}>
                {domainBlockReason}
              </p>
            )}
            <button type="button" className="inbox-pill-btn" onClick={() => setBlockModalOpen(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {isOwner && conversation.smsConsent !== true && conversation.smsConsentLedgerFailedAt && onRetryConsentLedger && (
        <div
          className="fb-callout"
          style={{ margin: '12px 24px 0', padding: '14px 16px' }}
        >
          <div style={{ fontSize: '13px', lineHeight: 1.55, marginBottom: '12px' }}>
            <strong>SMS consent record failed.</strong>
            {' '}The lead checked the box, but the consent ledger did not save. Retry to unlock texting.
          </div>
          <button
            type="button"
            className="inbox-pill-btn-primary"
            disabled={actionLoading}
            onClick={() => onRetryConsentLedger()}
          >
            Retry consent record
          </button>
        </div>
      )}

      {isBuyingHeld && (
        <div
          className="fb-callout"
          style={{ margin: '12px 24px 0', padding: '14px 16px' }}
        >
          <div style={{ fontSize: '13px', lineHeight: 1.55, marginBottom: '12px' }}>
            <strong>Looks like a buyer — AI reply is held for your OK.</strong>
            {' '}The spam layer held it because: {reasonsText}. Approve to send the AI reply, or block if this is not a real lead.
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {onApproveSuspicious && conversation.aiReplyHeld && (
              <button
                type="button"
                className="inbox-pill-btn-primary"
                disabled={actionLoading}
                onClick={() => onApproveSuspicious()}
              >
                Approve — send AI reply
              </button>
            )}
            {onMarkSpamAndBlock && (
              <button
                type="button"
                className="fb-btn-danger-sm"
                disabled={actionLoading}
                onClick={() => onMarkSpamAndBlock({ blockDomain: false })}
              >
                Not a buyer — block sender
              </button>
            )}
          </div>
        </div>
      )}

      {isSuspicious && (
        <div
          className="fb-callout warn"
          style={{ margin: '12px 24px 0', padding: '14px 16px' }}
        >
          <div style={{ fontSize: '13px', lineHeight: 1.55, marginBottom: '12px' }}>
            <strong>Flagged as likely spam because:</strong> {reasonsText}. The AI has not replied.
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {onApproveSuspicious && conversation.aiReplyHeld && (
              <button
                type="button"
                className="inbox-pill-btn-primary"
                disabled={actionLoading}
                onClick={() => onApproveSuspicious()}
              >
                It&apos;s real — send AI reply
              </button>
            )}
            {onMarkSpamAndBlock && (
              <button
                type="button"
                className="fb-btn-danger-sm"
                disabled={actionLoading}
                onClick={() => onMarkSpamAndBlock({ blockDomain: false })}
              >
                Spam — block sender
              </button>
            )}
          </div>
        </div>
      )}

      {isBlocked && (
        <div
          className="mono"
          style={{
            margin: '12px 24px 0',
            fontSize: '11px',
            letterSpacing: '0.06em',
            color: 'var(--danger)',
            background: 'color-mix(in srgb, var(--danger) 8%, transparent)',
            border: '1px solid color-mix(in srgb, var(--danger) 35%, transparent)',
            borderRadius: '8px',
            padding: '10px 14px',
          }}
        >
          BLOCKED — further inbound replies are recorded silently. Unblock to restore this thread to the active inbox.
        </div>
      )}

      {delegateOpen && (
        <div style={{ margin: '12px 24px 0', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={delegateTo}
            onChange={(e) => setDelegateTo(e.target.value)}
            style={{ padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--hairline-5)', fontSize: '13px' }}
          >
            <option value="">Select team member…</option>
            {(teamMembers || []).filter((m) => m.email).map((m) => (
              <option key={m.email} value={m.email}>{m.email}</option>
            ))}
          </select>
          <button
            type="button"
            className="inbox-pill-btn-primary"
            disabled={!delegateTo || actionLoading}
            onClick={async () => {
              const r = await onDelegate(delegateTo);
              if (r?.success) { setDelegateOpen(false); setDelegateTo(''); }
            }}
          >
            Assign
          </button>
          <button type="button" className="inbox-pill-btn" onClick={() => setDelegateOpen(false)}>Cancel</button>
        </div>
      )}

      {emailPickerOpen && (
        <div style={{ margin: '12px 24px 0', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="email"
            value={emailRecipient}
            onChange={(e) => onEmailRecipientChange(e.target.value)}
            placeholder="Recipient email"
            list="inbox-recent-emails"
            style={{ padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--hairline-5)', fontSize: '13px', flex: 1, minWidth: '200px' }}
          />
          <datalist id="inbox-recent-emails">
            {(recentEmails || []).map((e) => <option key={e} value={e} />)}
          </datalist>
          <button
            type="button"
            className="inbox-pill-btn-primary"
            disabled={!emailRecipient || actionLoading}
            onClick={async () => {
              const r = await onEmailToTeam();
              if (r?.success) setEmailPickerOpen(false);
            }}
          >
            Send
          </button>
          <button type="button" className="inbox-pill-btn" onClick={() => setEmailPickerOpen(false)}>Cancel</button>
        </div>
      )}

      {/* Thread */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {history.length === 0 && (
          <div style={{ textAlign: 'center', color: 'var(--dim)', fontSize: '13px' }}>No messages yet.</div>
        )}
        {history.map((msg, idx) => {
          const isLead = msg.type === 'lead';
          const isVoice = msg.channel === 'voice';
          return (
            <div key={idx} style={{ alignSelf: isLead ? 'flex-start' : 'flex-end', maxWidth: '62%', ...(isVoice ? { minWidth: 280 } : {}) }}>
              {isVoice ? (
                <div className={`${isLead ? 'inbox-bubble-lead' : 'inbox-bubble-operator'} inbox-bubble-voice`}>
                  <VoiceBubble
                    workspaceId={workspaceId}
                    callSid={msg.callSid}
                    label={msg.message || 'Voicemail'}
                    isOwner={isOwner}
                  />
                </div>
              ) : (
                <div
                  className={isLead ? 'inbox-bubble-lead' : 'inbox-bubble-operator'}
                  dangerouslySetInnerHTML={{ __html: renderMessageHtml(msg.message ?? '') }}
                />
              )}
              <div className="mono" style={{ fontSize: '10px', color: 'var(--faint)', marginTop: '4px', textAlign: isLead ? 'left' : 'right' }}>
                {`${formatBubbleTime(msg.timestamp)} \u00b7 ${messageChannelLabel(msg, idx, idx === firstLeadIdx)}`}
              </div>
            </div>
          );
        })}
        {isPaused && (
          <div
            className="mono"
            style={{
              alignSelf: 'center',
              fontSize: '10px',
              letterSpacing: '0.08em',
              color: 'var(--warn)',
              background: 'color-mix(in srgb, var(--warn) 10%, transparent)',
              border: '1px solid color-mix(in srgb, var(--warn) 35%, transparent)',
              borderRadius: '999px',
              padding: '5px 14px',
            }}
          >
            OPERATOR PAUSED · YOU ARE DRIVING
          </div>
        )}
      </div>

      {/* Composer */}
      <div style={{ padding: '16px 24px', borderTop: '1px solid var(--hairline-3)', background: 'var(--surface)' }}>
        <div style={{ border: '1px solid var(--hairline-5)', borderRadius: '12px', padding: '12px 16px' }}>
          <textarea
            value={composerValue}
            onChange={(e) => {
              if (composerMode === 'email') onEmailTextChange?.(e.target.value);
              else if (composerMode === 'sms') onSmsTextChange?.(e.target.value);
              else onNoteTextChange(e.target.value);
            }}
            placeholder={composerPlaceholder}
            disabled={actionLoading || (composerMode === 'sms' && !!smsDisabledReason)}
            rows={2}
            style={{
              width: '100%',
              border: 'none',
              outline: 'none',
              resize: 'none',
              fontSize: '14px',
              fontFamily: "'Archivo', Helvetica, Arial, sans-serif",
              background: 'transparent',
              color: 'var(--ink)',
            }}
          />
          {emailSendError && composerMode === 'email' && (
            <div className="mono" style={{ fontSize: '11px', color: 'var(--danger)', marginTop: '8px' }}>
              {emailSendError}
            </div>
          )}
          {smsSendError && composerMode === 'sms' && (
            <div className="mono" style={{ fontSize: '11px', color: 'var(--danger)', marginTop: '8px' }}>
              {smsSendError}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '12px', flexWrap: 'wrap', gap: '8px' }}>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button
                type="button"
                className={`inbox-composer-chip${INBOX_EMAIL_COMPOSER ? '' : ' disabled'}${composerMode === 'email' ? ' active' : ''}`}
                onClick={() => INBOX_EMAIL_COMPOSER && switchComposerMode('email')}
                disabled={!INBOX_EMAIL_COMPOSER}
                title={INBOX_EMAIL_COMPOSER ? undefined : 'Coming soon'}
              >
                EMAIL {INBOX_EMAIL_COMPOSER ? '' : '\u00b7 SOON'}
              </button>
              <button
                type="button"
                className={`inbox-composer-chip${composerMode === 'note' ? ' active' : ''}`}
                onClick={() => switchComposerMode('note')}
              >
                NOTE
              </button>
              <button
                type="button"
                className={`inbox-composer-chip${smsTab.disabled && !smsTab.setupHref ? ' disabled' : ''}${composerMode === 'sms' ? ' active' : ''}`}
                onClick={() => {
                  if (smsTab.setupHref) {
                    navigate(smsTab.setupHref);
                    return;
                  }
                  if (!smsTab.disabled) switchComposerMode('sms');
                }}
                disabled={smsTab.disabled && !smsTab.setupHref}
                title={smsTab.tooltip || (smsComposerEnabled ? smsDisabledReason || undefined : 'Coming soon')}
                style={smsTab.loading ? { opacity: 0.55 } : undefined}
              >
                {smsTab.label}
                {smsTab.loading && (
                  <span
                    aria-hidden="true"
                    style={{
                      display: 'inline-block',
                      width: '10px',
                      height: '10px',
                      marginLeft: '6px',
                      border: '1.5px solid currentColor',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'sms-tab-spin 0.7s linear infinite',
                      verticalAlign: 'middle',
                    }}
                  />
                )}
                {!smsTab.loading && smsTab.suffix ? ` \u00b7 ${smsTab.suffix}` : ''}
              </button>
              <style>{'@keyframes sms-tab-spin { to { transform: rotate(360deg); } }'}</style>
            </div>
            <button
              type="button"
              className="inbox-pill-btn-primary"
              disabled={!canSendNote && !canSendEmail && !canSendSms}
              onClick={() => {
                if (composerMode === 'note' && canSendNote) onAddNote();
                else if (composerMode === 'email' && canSendEmail) onSendEmail?.();
                else if (composerMode === 'sms' && canSendSms) onSendSms?.();
              }}
            >
              {composerMode === 'note' ? 'Add note' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const overflowItemStyle = {
  width: '100%',
  padding: '8px 10px',
  textAlign: 'left',
  background: 'transparent',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '13px',
  fontFamily: "'Archivo', sans-serif",
  color: 'var(--ink)',
};
